import { designTokens } from "@itandi/itandi-bb-ui";
import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Dropdown } from "@itandi/itandi-bb-ui/Dropdown";
import { DropdownMenu } from "@itandi/itandi-bb-ui/DropdownMenu";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { Sortable } from "@itandi/itandi-bb-ui/Sortable";
import { StatusChip } from "@itandi/itandi-bb-ui/StatusChip";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import Link from "next/link";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import { CompanyVendorModal } from "~/components/specifics/settings/company_vendors/CompanyVendorModal";
import { InviteCompanyVendorModal } from "~/components/specifics/settings/company_vendors/InviteCompanyVendorModal";
import type { CompanyVendor } from "~/models/CompanyVendor";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { PATH as CONSTRUCTION_ITEM_SETTING_PATH } from "~/pages/settings/company_vendors/[vendorCompanyId]/construction_item_settings";
import { isSuccess } from "~/utils/api/api_handler";
import { destroy, updateSeq } from "~/utils/api/CompanyVendor";
import { generatePath } from "~/utils/generatePath";

type Props = {
	companyVendors: CompanyVendor[];
	currentAccount: ItandiAccount;
};

type SortableItems = { data: CompanyVendor; id: string }[];

export function CompanyVendorList({ companyVendors, currentAccount }: Props) {
	const [editingVendor, setEditingVendor] = useState<CompanyVendor | null>(
		null
	);
	const [deletingVendor, setDeletingVendor] = useState<CompanyVendor | null>(
		null
	);
	const [invitingVendor, setInvitingVendor] = useState<CompanyVendor | null>(
		null
	);
	const canInviteCompanyVendor =
		!currentAccount.company?.fromMarket || currentAccount.isAdmin;

	const { trigger: seq } = useSWRMutation(
		generatePath("/company_vendors"),
		async (_key, { arg }: { arg: { sortedItems: CompanyVendor[] } }) => {
			return await updateSeq(arg.sortedItems);
		}
	);

	const sortableItems: SortableItems = companyVendors.map((item) => ({
		data: item,
		id: String(item.id)
	}));

	const onSortEnd = async (newItems: SortableItems): Promise<void> => {
		const sortedItems: CompanyVendor[] = newItems.map((item, index) => ({
			...item.data,
			seq: index + 1
		}));

		const result = await seq(
			{ sortedItems },
			{
				optimisticData: sortedItems,
				rollbackOnError: true
			}
		);

		if (!isSuccess(result)) {
			toast.error("並び順の更新に失敗しました。");
		}
	};

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/company_vendors"),
		async (_key, { arg }: { arg: { companyVendorId: number } }) => {
			return await destroy(arg.companyVendorId);
		}
	);

	const onDeleteCompanyVendor = useCallback(async () => {
		const result = await trigger({
			companyVendorId: deletingVendor!.id
		});

		if (isSuccess(result)) {
			toast.success("削除しました。");
			setDeletingVendor(null);
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	}, [deletingVendor, trigger]);

	const handleInviteVendor = (vendor: CompanyVendor) => {
		setInvitingVendor(vendor);
	};
	const handleCloseInvitingVendor = () => {
		setInvitingVendor(null);
	};
	const handleEditVendor = (vendor: CompanyVendor) => {
		setEditingVendor(vendor);
	};
	const handleCloseEditingVendor = () => {
		setEditingVendor(null);
	};
	const handleDeleteVendor = (vendor: CompanyVendor) => {
		setDeletingVendor(vendor);
	};
	const handleCloseDeletingVendor = () => {
		setDeletingVendor(null);
	};

	return (
		<div>
			<Table>
				<Thead>
					<Tr>
						<Th width={"3rem"}>並替</Th>
						<Th>施工会社</Th>
						<Th width={"10rem"} />
						<Th width={"6rem"}>単価</Th>
						<Th width={"5rem"}>操作</Th>
					</Tr>
				</Thead>
				<Tbody>
					<Sortable
						items={sortableItems}
						onSortEnd={onSortEnd}
						variant="vertical"
						withDragHandle
					>
						{({ dragHandle, item }) => (
							<Tr key={item.id}>
								<Td>
									<Flex justifyContent="center">{dragHandle}</Flex>
								</Td>
								<Td>{item.data.name}</Td>
								<Td>
									{canInviteCompanyVendor ? (
										item.data.vendorCompany !== null ? (
											<StatusChip variant="success">
												アカウント連携済
											</StatusChip>
										) : item.data.companyVendorPayload !== null ? (
											<StatusChip variant="info">招待中</StatusChip>
										) : (
											<StatusChip variant="default">
												アカウント未連携
											</StatusChip>
										)
									) : null}
								</Td>
								<Td>
									{item.data.vendorCompany !== null && (
										<TextLink
											as={Link}
											color="link"
											href={CONSTRUCTION_ITEM_SETTING_PATH(
												item.data.vendorCompany.id
											)}
										>
											単価一覧
										</TextLink>
									)}
								</Td>
								<Td>
									<Dropdown
										button={
											<Button height="small" type="button" variant="secondary">
												操作
											</Button>
										}
									>
										<DropdownMenu
											onClick={() => {
												handleEditVendor(item.data);
											}}
										>
											編集
										</DropdownMenu>
										{item.data.vendorCompany === null &&
											item.data.companyVendorPayload === null && (
												<DropdownMenu
													onClick={() => {
														handleInviteVendor(item.data);
													}}
												>
													アカウント連携
												</DropdownMenu>
											)}
										{item.data.planType === "saas" && (
											<DropdownMenu
												danger
												onClick={() => {
													handleDeleteVendor(item.data);
												}}
											>
												削除
											</DropdownMenu>
										)}
									</Dropdown>
								</Td>
							</Tr>
						)}
					</Sortable>
				</Tbody>
			</Table>
			<div>
				{invitingVendor ? (
					<InviteCompanyVendorModal
						companyVendor={invitingVendor}
						onClose={handleCloseInvitingVendor}
					/>
				) : null}
				{editingVendor ? (
					<CompanyVendorModal
						companyVendor={editingVendor}
						onClose={handleCloseEditingVendor}
					/>
				) : null}
				{deletingVendor ? (
					<Modalv2 isOpen onClose={handleCloseDeletingVendor} size="s">
						<ModalHeader title="施工会社の削除" />
						<ModalBody>
							施工会社を削除します。
							<Alert mt={designTokens["spacing/m"]} variant="warning">
								この操作は元に戻せません。
							</Alert>
						</ModalBody>
						<ModalFooter
							leftArea={
								<Button
									disabled={isMutating}
									onClick={handleCloseDeletingVendor}
									variant="secondary"
								>
									キャンセル
								</Button>
							}
							rightArea={
								<Button
									loading={isMutating}
									onClick={onDeleteCompanyVendor}
									variant="danger"
								>
									削除
								</Button>
							}
						/>
					</Modalv2>
				) : null}
			</div>
		</div>
	);
}
